import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { parse } from "query-string";
import React from "react";

import SimpleSelect from "@/mk2/apps/bazaar/components/SimpleSelect";
import { BrandCards } from "@/mk2/apps/strollers/components/BrandCards";
import { StrollersBuyingGuides } from "@/mk2/apps/strollers/components/StrollersBuyingGuides";
import { StrollersList } from "@/mk2/apps/strollers/components/StrollersList";
import { StrollersSearchInfos } from "@/mk2/apps/strollers/components/StrollersSearchInfos";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { StrollerSearchForm } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import { PageMode } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { Props } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearchPage";
import { convertStrollerType } from "@/mk2/apps/strollers/helpers";
import {
    strollersListUrl,
    strollersSearchByTypeUrl,
    strollersSearchUrl,
    strollersUrl,
} from "@/mk2/apps/strollers/urls";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn } from "@/mk2/components/Btn";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    STROLLERS_SEARCH,
    STROLLERS_SEARCH_FORM_EDIT,
    STROLLERS_SEARCH_NO_RESULTS,
    STROLLERS_SEARCH_REMOVE_ALL,
    STROLLERS_SEARCH_RESULTS_BRANDS,
    STROLLERS_SEARCH_RESULTS_results,
    STROLLERS_SEARCH_SORT_RESULTS_BY_NAME,
    STROLLERS_SEARCH_SORT_RESULTS_BY_PRICE_ASC,
    STROLLERS_SEARCH_SORT_RESULTS_BY_PRICE_DESC,
    STROLLERS_SEARCH_SORT_RESULTS_BY_RATING,
    STROLLERS_SEARCH_SORT_RESULTS_BY_RECOMMENDED,
    STROLLER_STROLLEROPEDIA,
    count_STROLLERS,
} from "@/mk/autogenerated/translations/StrollersSearch.981c11f9421b1a539b0aa964142434d9";

import styles from "./StrollersSearch.mscss";

interface OwnState {
    sortResultsBy: SortResultsBy;
}

enum SortResultsBy {
    NAME = "name", // A-Z (bo encyklopedie)
    RATING = "-user_score", // Nejlépe hodnocené
    PRICE_ASC = "price", // Nejlevnější
    PRICE_DESC = "-price", // Nejdražší
    RECOMMENDED_CHOICE = "recommended_choice", // Doporučené redakcí

    // SortResultsBy.DATE not yet displayed (https://trello.com/c/dFe9MvAH/)
    // DATE = 'date', // Nejnovější
}

const sortOptions: Record<SortResultsBy, string> = {
    [SortResultsBy.RECOMMENDED_CHOICE]: STROLLERS_SEARCH_SORT_RESULTS_BY_RECOMMENDED,
    [SortResultsBy.RATING]: STROLLERS_SEARCH_SORT_RESULTS_BY_RATING,
    [SortResultsBy.PRICE_ASC]: STROLLERS_SEARCH_SORT_RESULTS_BY_PRICE_ASC,
    [SortResultsBy.PRICE_DESC]: STROLLERS_SEARCH_SORT_RESULTS_BY_PRICE_DESC,
    [SortResultsBy.NAME]: STROLLERS_SEARCH_SORT_RESULTS_BY_NAME,

    // [SortResultsBy.DATE]: STROLLERS_SEARCH_SORT_RESULTS_BY_DATE,
};

export class StrollersSearch extends React.Component<Props, OwnState> {
    private static getBreadcrumbItems() {
        const breadcrumbLinks = [];
        breadcrumbLinks.push(
            <Link className={styles.BreadcrumbItem} key="strolleropedia" to={strollersUrl}>
                {STROLLER_STROLLEROPEDIA}
            </Link>,
        );
        breadcrumbLinks.push(<>{STROLLERS_SEARCH}</>);
        return breadcrumbLinks;
    }

    public state: OwnState = {
        sortResultsBy: SortResultsBy.RECOMMENDED_CHOICE,
    };

    public componentDidMount() {
        const {
            location: { search },
        } = this.props;

        const parsedSearch = parse(search);
        if (parsedSearch.order) {
            this.setState({
                sortResultsBy: parsedSearch.order as SortResultsBy,
            });
        }
    }

    public render() {
        const {
            brands, // all brands
            brandsCount,
            brandsOfQuery,
            count,
            strollersCountPerBrand,
            reviewsCountPerBrand,
            hasMore,
            initialValues,
            isLandingPageForStrollerType,
            formData,
            queryString,
            stringFacetItems,
            renderedFacetItems,
            items,
            strollersPreviewPhotoMap,
            variantsPreviewPhotoMap,
            strollersPhotosMap,
            strollerIdToVariantsMap,
            loadingState,
            routeParams: { pageMode },
            onSave,
            onToggleMoreFacets,
            onStrollersComparisonToggle,
            showAdditionalFacets,
            comparison,
            searchInfos,
            buyingGuide,
            location,
        } = this.props;

        const renderedBrandsOfQuery: React.ReactNode[] = [];

        if (
            pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY ||
            pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE
        ) {
            if (formData.brand.length) {
                brandsOfQuery.map((brand, ii) => {
                    const logoPhoto = brand.logoPhoto || dummyPhoto;
                    renderedBrandsOfQuery.push(
                        <Link key={ii} to={url(strollersListUrl, { slug: brand.slug })}>
                            <ImgCropped
                                width={110}
                                height={50}
                                mode={ImgCropMode.Embed}
                                imgWidth={logoPhoto.originalWidth}
                                imgHeight={logoPhoto.originalHeight}
                                imgUrl={logoPhoto.url250x250}
                                alt={brand.name}
                                lazy={false}
                            />
                        </Link>,
                    );
                });
            }
        }
        return (
            <div className={styles.StrollersSearch}>
                {pageMode === PageMode.SHOW_SEARCH_FORM && (
                    <StrollerSearchForm
                        initialValues={initialValues}
                        onToggleMoreFacets={onToggleMoreFacets}
                        showAdditionalFacets={showAdditionalFacets}
                        onSave={onSave}
                    />
                )}
                {(pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY ||
                    pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE) && (
                    <>
                        <BreadcrumbSiteStructure
                            items={StrollersSearch.getBreadcrumbItems()}
                            className={styles.StrollersSearch__breadcrumb}
                        />

                        <h1 className={styles.StrollersSearch__header}>
                            {isLandingPageForStrollerType ? (
                                <>{isLandingPageForStrollerType}</>
                            ) : (
                                <Interpolate
                                    i18nKey={STROLLERS_SEARCH_RESULTS_results}
                                    results={stringFacetItems.join(", ")}
                                />
                            )}
                        </h1>
                        {Boolean(count) && (
                            <div className={styles.StrollersSearch__hits}>
                                ({interpolate(count_STROLLERS, { count })})
                            </div>
                        )}
                        {Boolean(searchInfos?.length) && (
                            <StrollersSearchInfos searchInfos={searchInfos} />
                        )}
                        {Boolean(renderedBrandsOfQuery?.length) && (
                            <div className={styles.StrollersSearch__brandsOfQuery}>
                                <div className={styles.StrollersSearch__brandsOfQuery__header}>
                                    {STROLLERS_SEARCH_RESULTS_BRANDS}
                                </div>
                                <div className={styles.StrollersSearch__brandsOfQuery__list}>
                                    {renderedBrandsOfQuery}
                                </div>
                            </div>
                        )}
                        {buyingGuide && (
                            <StrollersBuyingGuides
                                buyingGuideContentItem={buyingGuide}
                                className={styles.StrollersSearch__buyingGuides}
                            />
                        )}
                        {(pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY ||
                            pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE) && (
                            <>
                                <div className={styles.StrollersSearch__editSearch}>
                                    <Btn
                                        icon={faSearch}
                                        link={url(
                                            strollersSearchUrl,
                                            { pageMode: PageMode.SHOW_SEARCH_FORM },
                                            parse(queryString),
                                        )}
                                        label={STROLLERS_SEARCH_FORM_EDIT}
                                    />
                                </div>
                                <div
                                    className={styles.StrollersSearch__facetSummaryHeaderContainer}
                                >
                                    <div className={styles.StrollersSearch__facetsLine}>
                                        {renderedFacetItems}
                                        {Boolean(renderedFacetItems?.length) && (
                                            <Link
                                                className={styles.StrollersSearch__clearSearch}
                                                to={strollersUrl}
                                            >
                                                {STROLLERS_SEARCH_REMOVE_ALL}
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        <div className={styles.StrollersSearch__sorting}>
                            <SimpleSelect
                                type="bigger"
                                selected={this.state.sortResultsBy}
                                options={sortOptions}
                                onChange={this.onSortResultsByChange}
                            />
                        </div>
                        <div className={styles.StrollersSearch__searchResultsContainer}>
                            <StrollersList
                                loadingState={loadingState}
                                items={items}
                                strollerIdToVariantsMap={strollerIdToVariantsMap}
                                hasMore={hasMore}
                                noResultMessage={STROLLERS_SEARCH_NO_RESULTS}
                                comparison={comparison}
                                strollersPreviewPhotoMap={strollersPreviewPhotoMap}
                                variantsPreviewPhotoMap={variantsPreviewPhotoMap}
                                photoObjectsMap={strollersPhotosMap}
                                loadNext={this.loadNext}
                                load={this.load}
                                onComparisonToggle={onStrollersComparisonToggle}
                                count={count}
                                searchData={formData}
                                location={location}
                            />
                        </div>
                        {strollersCountPerBrand && (
                            <BrandCards
                                brands={brands}
                                brandsCount={brandsCount}
                                strollersCountPerBrand={strollersCountPerBrand}
                                reviewsCountPerBrand={reviewsCountPerBrand}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }

    private load = () => {
        const { onLoad, onStrollersComparisonLoad, queryString } = this.props;

        onLoad(queryString);
        onStrollersComparisonLoad();
    };

    private loadNext = () => {
        const { items, loadingState, onLoad, queryString } = this.props;

        // Do not trigger next load
        if (loadingState === LoadingState.LOADING) {
            return;
        }

        onLoad(queryString, items.length);

        // Debounce loadMoreRows
        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private onSortResultsByChange = (value) => {
        const { routeNavigate, queryString } = this.props;

        this.setState({
            sortResultsBy: value,
        });

        const parsedSearch = parse(queryString);
        parsedSearch.order = value;
        routeNavigate(considerStrollersSearchResultUrl(parsedSearch));
    };
}

export const considerStrollersSearchResultUrl = (data) => {
    const considerKeys = Object.keys(data).filter((name) => {
        return !(
            // Array of values
            (
                (Array.isArray(data[name]) && data[name].length === 0) ||
                // Single value
                (!Array.isArray(data[name]) && !data[name]) ||
                // Ignore availability
                (name === "availability" && data[name] === "available")
            )
        );
    });

    if (considerKeys.length === 1 && considerKeys[0] === "type" && data.type.length === 1) {
        return url(strollersSearchByTypeUrl, {
            pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE,
            strollerType: convertStrollerType(data.type[0], "_to-"),
        });
    } else {
        const pageMode = PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY;
        return url(strollersSearchUrl, { pageMode }, data);
    }
};
